// ═══════════════════════════════════════════════════
// Articulos.jsx — Página pública listado de artículos
// ═══════════════════════════════════════════════════
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuthUser } from '../../context/AuthUserContext';
import './Articulos.css';

const API = 'https://centrocomunicar.com/api-comunicar/endpoints/articulos.php';

const LogoComunicar = ({ height = 40 }) => (
  <svg height={height} viewBox="0 0 300 52" fill="none" xmlns="http://www.w3.org/2000/svg">
    <ellipse cx="20" cy="28" rx="14" ry="12" fill="#82D8B3" opacity="0.7"/>
    <path d="M10 38 Q6 44 4 48 Q11 43 16 40Z" fill="#82D8B3" opacity="0.7"/>
    <ellipse cx="34" cy="22" rx="20" ry="17" fill="#82D8B3"/>
    <path d="M20 36 Q16 44 13 48 Q22 42 27 38Z" fill="#82D8B3"/>
    <circle cx="27" cy="22" r="2.2" fill="white"/>
    <circle cx="34" cy="22" r="2.2" fill="white"/>
    <circle cx="41" cy="22" r="2.2" fill="white"/>
    <text x="62" y="24" fontFamily="'DM Sans', sans-serif" fontWeight="700" fontSize="16" fill="#4db88a">Centro Comunicar</text>
    <text x="64" y="38" fontFamily="'DM Sans', sans-serif" fontWeight="400" fontSize="10" fill="#888">Comunicar – Lenguaje, Aprendizaje y Habla</text>
  </svg>
);

const IconArrow    = () => (<svg viewBox="0 0 20 20" fill="none" width="15" height="15"><path d="M4 10h12M11 6l5 4-5 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>);
const IconSearch   = () => (<svg viewBox="0 0 24 24" fill="none" width="16" height="16"><circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/><path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>);
const IconCalendar = () => (<svg viewBox="0 0 24 24" fill="none" width="13" height="13"><rect x="3" y="4" width="18" height="18" rx="3" stroke="currentColor" strokeWidth="2"/><path d="M3 9h18M8 2v4M16 2v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>);
const IconEye      = () => (<svg viewBox="0 0 24 24" fill="none" width="13" height="13"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="2"/><circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/></svg>);

const CAT_COLORS = {
  Lenguaje: '#4db88a', TEA: '#60a5fa', Habla: '#fbbf24',
  Aprendizaje: '#a78bfa', Familia: '#f9a8d4', Tartamudez: '#fb7185',
  Recursos: '#34d399', General: '#9ca3af',
};

function ArticuloCard({ art, destacado = false }) {
  const color = CAT_COLORS[art.categoria] || '#82D8B3';
  const fecha = new Date(art.created_at).toLocaleDateString('es-PE', { day: '2-digit', month: 'short', year: 'numeric' });

  return (
    <Link to={`/articulos/${art.slug}`} className={`art-card ${destacado ? 'art-card--destacado' : ''}`}>
      <div className="art-card__thumb">
        {art.imagen_portada
          ? <img src={art.imagen_portada} alt={art.titulo}/>
          : <div className="art-card__thumb-placeholder" style={{ background: `linear-gradient(135deg, ${color}22, ${color}55)` }}>
              <span style={{ fontSize: '2rem' }}>📝</span>
            </div>
        }
        {destacado && <span className="art-card__badge-dest">⭐ Destacado</span>}
        {art.categoria && (
          <span className="art-card__badge-cat" style={{ background: color }}>
            {art.categoria}
          </span>
        )}
      </div>
      <div className="art-card__body">
        <h3 className="art-card__titulo">{art.titulo}</h3>
        {art.resumen && <p className="art-card__resumen">{art.resumen.slice(0, 120)}{art.resumen.length > 120 ? '…' : ''}</p>}
        <div className="art-card__meta">
          <span><IconCalendar/> {fecha}</span>
          <span><IconEye/> {art.vistas || 0} vistas</span>
          {art.autor && <span>✍️ {art.autor}</span>}
        </div>
        {art.tags?.length > 0 && (
          <div className="art-card__tags">
            {art.tags.slice(0, 3).map(tag => (
              <span key={tag} className="art-card__tag">#{tag}</span>
            ))}
          </div>
        )}
        <span className="art-card__leer">Leer artículo <IconArrow/></span>
      </div>
    </Link>
  );
}

export default function Articulos() {
  const { estaLogueado, usuario } = useAuthUser();
  const [articulos,   setArticulos]   = useState([]);
  const [categorias,  setCategorias]  = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [total,       setTotal]       = useState(0);
  const [busqueda,    setBusqueda]    = useState('');
  const [catActiva,   setCatActiva]   = useState('');
  const [tagActivo,   setTagActivo]   = useState('');
  const [offset,      setOffset]      = useState(0);
  const LIMIT = 9;

  const fetchArticulos = useCallback(async (reset = false) => {
    setLoading(true);
    try {
      const off = reset ? 0 : offset;
      const params = new URLSearchParams({ action: 'list', limit: LIMIT, offset: off });
      if (busqueda)  params.set('busqueda', busqueda);
      if (catActiva) params.set('categoria', catActiva);
      if (tagActivo) params.set('tag', tagActivo);
      const r = await fetch(`${API}?${params}`);
      const j = await r.json();
      if (j.success) {
        setArticulos(prev => reset ? (j.data?.items || []) : [...prev, ...(j.data?.items || [])]);
        setTotal(j.data?.total || 0);
        if (reset) setOffset(LIMIT);
        else setOffset(off + LIMIT);
      }
    } catch {}
    finally { setLoading(false); }
  }, [busqueda, catActiva, tagActivo, offset]);

  useEffect(() => {
    fetch(`${API}?action=categorias`).then(r => r.json()).then(j => {
      if (j.success) setCategorias(j.data || []);
    }).catch(() => {});
  }, []);

  useEffect(() => { fetchArticulos(true); }, [busqueda, catActiva, tagActivo]);

  const destacados = articulos.filter(a => a.destacado);
  const normales   = articulos.filter(a => !a.destacado);

  return (
    <div className="art-page">

      <nav className="nav">
        <Link to="/" className="nav__logo"><LogoComunicar height={44}/></Link>
        <div className="nav__links">
          <Link to="/">Inicio</Link>
          <Link to="/formaciones">Cursos</Link>
          <Link to="/recursos">Materiales</Link>
          <Link to="/articulos" style={{ color: 'var(--verde-dk,#4db88a)', fontWeight: 700 }}>Artículos</Link>
          <Link to="/comunidad">Comunidad</Link>
          <Link to="/articulos">Artículos</Link>

          {estaLogueado
            ? <Link to="/mi-cuenta" className="nav__mi-cuenta">● {usuario.nombre}</Link>
            : <Link to="/login" className="nav__login">Iniciar sesión</Link>
          }
        </div>
      </nav>

      {/* Hero */}
      <div className="art-hero">
        <p className="section-label">Blog y recursos</p>
        <h1>Artículos <em>especializados</em></h1>
        <p className="art-hero__sub">Contenido sobre lenguaje, comunicación y aprendizaje para profesionales y familias.</p>

        {/* Buscador */}
        <div className="art-search">
          <IconSearch/>
          <input
            type="text" placeholder="Buscar artículos…"
            value={busqueda}
            onChange={e => { setBusqueda(e.target.value); setOffset(0); }}
          />
        </div>
      </div>

      {/* Categorías */}
      {categorias.length > 0 && (
        <div className="art-cats">
          <button className={`art-cat-btn ${!catActiva ? 'art-cat-btn--active' : ''}`} onClick={() => setCatActiva('')}>
            Todos
          </button>
          {categorias.map(c => (
            <button key={c.categoria}
              className={`art-cat-btn ${catActiva === c.categoria ? 'art-cat-btn--active' : ''}`}
              style={catActiva === c.categoria ? { background: CAT_COLORS[c.categoria] || '#4db88a', color: '#fff', borderColor: 'transparent' } : {}}
              onClick={() => { setCatActiva(c.categoria); setOffset(0); }}
            >
              {c.categoria} <span className="art-cat-count">{c.total}</span>
            </button>
          ))}
        </div>
      )}

      {tagActivo && (
        <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
          <span style={{ background: '#edfaf4', color: '#4db88a', padding: '.3rem .8rem', borderRadius: 100, fontSize: '.82rem', fontWeight: 700 }}>
            #{tagActivo}
            <button style={{ background: 'none', border: 'none', color: '#4db88a', cursor: 'pointer', marginLeft: '.4rem', fontWeight: 700 }}
              onClick={() => setTagActivo('')}>✕</button>
          </span>
        </div>
      )}

      <div className="art-body">

        {/* Destacados */}
        {!busqueda && !catActiva && !tagActivo && destacados.length > 0 && (
          <section className="art-section">
            <div className="art-section__head">
              <h2>⭐ Destacados</h2>
            </div>
            <div className="art-grid art-grid--destacados">
              {destacados.slice(0, 2).map(a => <ArticuloCard key={a.id} art={a} destacado/>)}
            </div>
          </section>
        )}

        {/* Todos */}
        <section className="art-section">
          {(!busqueda && !catActiva && !tagActivo && destacados.length > 0) && (
            <div className="art-section__head">
              <h2>Todos los artículos</h2>
              <span className="art-count">{total} artículos</span>
            </div>
          )}
          {loading && articulos.length === 0 ? (
            <div className="art-grid">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="art-card art-card--skeleton">
                  <div className="art-card__thumb" style={{ background: '#e8f5ef' }}/>
                  <div className="art-card__body">
                    <div className="skel skel--line skel--long"/>
                    <div className="skel skel--line skel--mid"/>
                  </div>
                </div>
              ))}
            </div>
          ) : normales.length === 0 && articulos.length === 0 ? (
            <div className="art-empty">
              <p>{busqueda || catActiva || tagActivo ? 'Sin resultados para tu búsqueda.' : 'No hay artículos publicados aún.'}</p>
            </div>
          ) : (
            <div className="art-grid">
              {(busqueda || catActiva || tagActivo ? articulos : normales).map(a => (
                <ArticuloCard key={a.id} art={a}/>
              ))}
            </div>
          )}

          {/* Cargar más */}
          {!loading && articulos.length < total && (
            <div style={{ textAlign: 'center', marginTop: '2rem' }}>
              <button className="art-load-more" onClick={() => fetchArticulos(false)}>
                Cargar más artículos
              </button>
            </div>
          )}
          {loading && articulos.length > 0 && (
            <div style={{ textAlign: 'center', padding: '1rem', color: '#5a6a63' }}>Cargando…</div>
          )}
        </section>
      </div>

      <footer className="footer">
        <div className="footer__bottom" style={{ textAlign: 'center', padding: '2rem' }}>
          <p>© 2026 Comunicar – Desarrollo y Lenguaje. Todos los derechos reservados.</p>
        </div>
      </footer>
    </div>
  );
}


// ═══════════════════════════════════════════════════
// ArticuloDetalle.jsx — Página de detalle de artículo
// ═══════════════════════════════════════════════════
// Este componente va en un archivo separado: ArticuloDetalle.jsx

/*
import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuthUser } from '../../context/AuthUserContext';
import './ArticuloDetalle.css';

const API = 'https://centrocomunicar.com/api-comunicar/endpoints/articulos.php';

const CAT_COLORS = {
  Lenguaje: '#4db88a', TEA: '#60a5fa', Habla: '#fbbf24',
  Aprendizaje: '#a78bfa', Familia: '#f9a8d4', Tartamudez: '#fb7185',
  Recursos: '#34d399', General: '#9ca3af',
};

export default function ArticuloDetalle() {
  const { slug }    = useParams();
  const navigate    = useNavigate();
  const { estaLogueado, usuario } = useAuthUser();
  const [art,     setArt]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied,  setCopied]  = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchArt = async () => {
      setLoading(true);
      try {
        const r = await fetch(`${API}?action=detail&slug=${slug}`);
        const j = await r.json();
        if (!j.success) { navigate('/articulos'); return; }
        setArt(j.data);
        // Sumar vista
        fetch(`${API}?action=sumar_vista`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: j.data.id }),
        }).catch(() => {});
      } catch { navigate('/articulos'); }
      finally { setLoading(false); }
    };
    fetchArt();
  }, [slug]);

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getYoutubeId = (url) => {
    if (!url) return null;
    const m = url.match(/(?:youtu\.be\/|v=)([^&]+)/);
    return m ? m[1] : url;
  };

  if (loading) return (
    <div className="artd-page">
      <div style={{ textAlign: 'center', padding: '5rem', color: '#5a6a63' }}>Cargando artículo…</div>
    </div>
  );

  if (!art) return null;

  const fecha = new Date(art.created_at).toLocaleDateString('es-PE', { day: '2-digit', month: 'long', year: 'numeric' });
  const color = CAT_COLORS[art.categoria] || '#82D8B3';
  const youtubeId = getYoutubeId(art.video_youtube);

  return (
    <div className="artd-page">

      <nav className="nav">
        <Link to="/" className="nav__logo"><LogoComunicar height={44}/></Link>
        <div className="nav__links">
          <Link to="/">Inicio</Link>
          <Link to="/formaciones">Cursos</Link>
          <Link to="/recursos">Materiales</Link>
          <Link to="/articulos" style={{ color: 'var(--verde-dk,#4db88a)', fontWeight: 700 }}>Artículos</Link>
          {estaLogueado
            ? <Link to="/mi-cuenta" className="nav__mi-cuenta">● {usuario.nombre}</Link>
            : <Link to="/login" className="nav__login">Iniciar sesión</Link>
          }
        </div>
      </nav>

      <div className="artd-breadcrumb">
        <Link to="/articulos">← Artículos</Link>
        {art.categoria && <><span>/</span><span>{art.categoria}</span></>}
      </div>

      <article className="artd-body">

        <header className="artd-header">
          {art.categoria && (
            <span className="artd-cat" style={{ background: color }}>{art.categoria}</span>
          )}
          <h1 className="artd-titulo">{art.titulo}</h1>
          <div className="artd-meta">
            <span>✍️ {art.autor}</span>
            <span>📅 {fecha}</span>
            <span>👁 {art.vistas} vistas</span>
          </div>
          {art.resumen && <p className="artd-resumen">{art.resumen}</p>}
          <div className="artd-share">
            <button onClick={copyLink} className="artd-share-btn">
              {copied ? '✓ Copiado' : '🔗 Copiar enlace'}
            </button>
            <a
              href={`https://wa.me/?text=${encodeURIComponent(art.titulo + ' ' + window.location.href)}`}
              target="_blank" rel="noopener noreferrer" className="artd-share-btn artd-share-btn--wa"
            >
              📲 Compartir por WhatsApp
            </a>
          </div>
        </header>

        {art.imagen_portada && (
          <img src={art.imagen_portada} alt={art.titulo} className="artd-portada"/>
        )}

        {youtubeId && (
          <div className="artd-video">
            <iframe
              src={`https://www.youtube.com/embed/${youtubeId}`}
              title={art.titulo} allowFullScreen
              style={{ width: '100%', height: '100%', border: 'none' }}
            />
          </div>
        )}

        {art.contenido && (
          <div className="artd-contenido" dangerouslySetInnerHTML={{ __html: art.contenido }}/>
        )}

        {art.imagenes?.length > 0 && (
          <div className="artd-galeria">
            <h3>Galería</h3>
            <div className="artd-galeria__grid">
              {art.imagenes.map(img => (
                <a key={img.id} href={img.url} target="_blank" rel="noopener noreferrer">
                  <img src={img.url} alt=""/>
                </a>
              ))}
            </div>
          </div>
        )}

        {art.tags?.length > 0 && (
          <div className="artd-tags">
            {art.tags.map(tag => (
              <Link key={tag} to={`/articulos?tag=${tag}`} className="artd-tag">#{tag}</Link>
            ))}
          </div>
        )}

      </article>

      {art.relacionados?.length > 0 && (
        <section className="artd-relacionados">
          <h2>Artículos relacionados</h2>
          <div className="artd-rel-grid">
            {art.relacionados.map(rel => (
              <Link key={rel.id} to={`/articulos/${rel.slug}`} className="artd-rel-card">
                {rel.imagen_portada && <img src={rel.imagen_portada} alt={rel.titulo}/>}
                <div>
                  <strong>{rel.titulo}</strong>
                  {rel.resumen && <p>{rel.resumen.slice(0, 80)}…</p>}
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <footer className="footer">
        <div className="footer__bottom" style={{ textAlign: 'center', padding: '2rem' }}>
          <p>© 2026 Comunicar – Desarrollo y Lenguaje. Todos los derechos reservados.</p>
        </div>
      </footer>
    </div>
  );
}
*/